import { Component, Input } from "@angular/core";
import { CryptowalletDetailsComponent } from "../../wallet-edit/wallet-details/cryptowallet-details.component";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { TradableTileCoreComponent } from "../tradable-tile-core.component";
import { Index } from "@models/asset/index/index.model";
import { Helper } from "@helpers/functions";
import { AccountService } from "@modules/account/services/account.service";
import { UserService } from "@main-services/user.service";
import { WalletModalComponent } from "../../wallet-modal/wallet-modal.component";
import { Cryptocoin, CryptoWallet } from "@models";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { ModalService } from "@core-services/modal.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";

@Component({
	selector: "tradable-wallet",
	templateUrl: "./tradable-to-wallet.component.html",
	styleUrls: ["./tradable-to-wallet.component.scss"],
	standalone: false
})
export class TradableToWalletComponent extends TradableTileCoreComponent {
	// ----------------------------
	//
	// This component is only used if you want to directly select wallets (instead of Tradables)
	//
	// ----------------------------

	@Input() public tradableWallets: TradableWallet[] = [];
	@Input() public createNewWallet: boolean = false;
	@Input() public showPendingTransactionCounter: boolean = false;
	@Input() public disabled: boolean = false;
	@Input() public isDashboard: boolean = false;

	@Input()
	public set hideEmptyWallets(b: boolean) {
		if (b !== this._hideEmptyWallets) {
			this._hideEmptyWallets = b;
			this.updateViewItems(); // View items updated on dashboard hide
		}
	}

	public get hideEmptyWallets(): boolean {
		return this._hideEmptyWallets;
	}

	private _hideEmptyWallets: boolean = false;

	@Input()
	public set searchInput(s: string) {
		if (s !== this.searchInputValue) {
			this.searchInputValue = s;
			this.updateViewItems(); // View items updated on dashboard search
		}
	}

	public get searchInput(): string {
		return this.searchInputValue;
	}

	public constructor(
		protected accountService: AccountService,
		protected modalService: ModalService,
		protected walletService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected userService: UserService
	) {
		super(accountService, modalService, walletService, tradableListService, userService);
	}

	public createsNewWallet(): boolean {
		return this.createNewWallet;
	}

	public updateViewItems(): void {
		let wallets: TradableWallet[] = this.tradableWallets;
		if (this.searchInputValue) {
			wallets = Helper.filterBySearch(wallets, this.searchInputValue, ["name"]);
		} // This search filters out tradables that are not being searched for which removes the respective wallets from the display
		wallets = wallets.filter((wallet) => this.getTradableByWallet(wallet) instanceof this.filterType);
		if (this.hideEmptyWallets) {
			wallets = wallets.filter((wallet) => wallet.getBalance() > 0);
		}
		this.walletList.next(wallets);
	}

	public onClick(w: TradableWallet): void {
		if (this.disabled || !this.isActive(w)) {
			return;
		}
		this.onSelect.emit(w);
	}

	// ------------ Methods for special wallet-tile display below

	public showDepositQR(item: TradableWallet): boolean {
		return this.getTradableByWallet(item) instanceof Cryptocoin && this.isDashboard;
	}

	public openWalletDetails($event: Event, walletID: string): void {
		$event.stopPropagation();
		const wallet: TradableWallet = this.walletService.getItemById(walletID);
		this.modalService.create(CryptowalletDetailsComponent, { data: wallet }).present();
	}

	public generateNewWallet(): void {
		const wallet: CryptoWallet = new CryptoWallet();
		wallet.setCurrencyId("1");
		this.modalService.showModal(WalletModalComponent, wallet);
	}

	public getTransactionCounter(wallet: TradableWallet): boolean {
		if (wallet) {
			if (this.showPendingTransactionCounter) {
				return wallet.getPendingTransactionCount() > 0;
			}
		} else {
			return false;
		}
	}

	public getTooltip(w: TradableWallet): string {
		if (this.disabled) {
			return this.action + "_not_active";
		}

		if (this.isActive(w)) {
			return this.tradableListService
				.serviceForObject(this.getTradableByWallet(w))
				.getTradableToolTip("", this.getTradableByWallet(w), false);
		}

		return "";
	}

	public isActive(cw: TradableWallet): boolean {
		const coin: Tradable = this.getTradableByWallet(cw);
		if (!this.action) {
			return true;
		}
		if (coin.hasOwnProperty(this.action + "_active")) {
			return coin[this.action + "_active"];
		}

		return true;
	}

	// ------------ Utility Methods below

	public isIndexItem(item: TradableWallet): boolean {
		return this.getTradableByWallet(item) instanceof Index;
	}

	public getWalletSumFiat(c: TradableWallet): number {
		return this.calculateWalletSum(c.getBalance(), this.getTradableByWallet(c));
	}

	public getTradableByWallet(a: TradableWallet): Tradable {
		return this.tradableListService.getItemById(a.getCurrencyId());
	}
}
